<template>
  <div class="agent-whatsapp-view">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <router-link to="/agent-dashboard" class="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </router-link>
        <div>
          <h1>WhatsApp Conversations</h1>
          <p class="subtitle">Manage customer chats</p>
        </div>
      </div>
      <button class="refresh-button" @click="loadConversations" :disabled="isLoading">
        <svg :class="{ spinning: isLoading }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
        Refresh
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group">
        <label>Status</label>
        <select v-model="filters.status" @change="loadConversations">
          <option value="">All Conversations</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Search</label>
        <div class="search-wrapper">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <input
            type="text"
            v-model="filters.search"
            @input="debounceSearch"
            placeholder="Search by name or phone..."
          />
        </div>
      </div>
    </div>

    <!-- Conversations Layout -->
    <div class="conversations-layout">
      <!-- Conversation List -->
      <div class="conversations-list">
        <div v-if="isLoading" class="loading-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" opacity="0.25"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round" class="spinner"/>
          </svg>
          <p>Loading conversations...</p>
        </div>

        <div v-else-if="filteredConversations.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.2">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
          <h3>No conversations</h3>
          <p>No WhatsApp conversations found</p>
        </div>

        <div
          v-else
          v-for="conversation in filteredConversations"
          :key="conversation.id"
          :class="['conversation-item', { active: selectedConversation?.id === conversation.id }]"
          @click="selectConversation(conversation)"
        >
          <div class="conversation-avatar">
            {{ getInitials(conversation.customer_name) }}
          </div>
          <div class="conversation-content">
            <div class="conversation-header">
              <h3>{{ conversation.customer_name }}</h3>
              <span class="conversation-time">{{ formatTime(conversation.last_message_at) }}</span>
            </div>
            <div class="conversation-preview">
              <p>{{ conversation.last_message }}</p>
              <span v-if="conversation.unread_count > 0" class="unread-badge">
                {{ conversation.unread_count }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Panel -->
      <div class="chat-panel">
        <div v-if="!selectedConversation" class="no-conversation-selected">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" opacity="0.2">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
          <h3>Select a conversation</h3>
          <p>Choose a conversation from the list to view messages</p>
        </div>

        <template v-else>
          <!-- Chat Header -->
          <div class="chat-header">
            <div class="chat-header-info">
              <div class="conversation-avatar large">
                {{ getInitials(selectedConversation.customer_name) }}
              </div>
              <div>
                <h2>{{ selectedConversation.customer_name }}</h2>
                <p>{{ selectedConversation.phone_number }}</p>
              </div>
            </div>
            <div class="chat-header-actions">
              <button class="icon-button" @click="markAsResolved">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Messages Area -->
          <div class="messages-area" ref="messagesArea">
            <div v-for="message in messages" :key="message.id" :class="['message', message.direction]">
              <div class="message-bubble">
                <p>{{ message.text }}</p>
                <span class="message-time">{{ formatMessageTime(message.timestamp) }}</span>
              </div>
            </div>
          </div>

          <!-- Message Input -->
          <div class="message-input-area">
            <div class="input-wrapper">
              <button class="icon-button">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                </svg>
              </button>
              <textarea
                v-model="messageText"
                placeholder="Type a message..."
                @keydown.enter.exact.prevent="sendMessage"
                rows="1"
              ></textarea>
              <button class="send-button" @click="sendMessage" :disabled="!messageText.trim()">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { requestNotificationPermission, onMessageListener, saveAgentFCMTokenToBackend } from '../services/firebase'

export default {
  name: 'AgentWhatsAppView',
  data () {
    return {
      conversations: [],
      selectedConversation: null,
      messages: [],
      filters: {
        status: '',
        search: ''
      },
      messageText: '',
      isLoading: false,
      searchDebounce: null,
      // Notification
      notificationSound: null,
      notificationEnabled: true,
      audioUnlocked: false,
      fcmToken: null,
      fcmInitialized: false
    }
  },
  computed: {
    filteredConversations () {
      let filtered = [...this.conversations]

      // Filter by status
      if (this.filters.status) {
        filtered = filtered.filter(conv => conv.status === this.filters.status)
      }

      // Filter by search
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(conv =>
          conv.customer_name?.toLowerCase().includes(search) ||
          conv.phone_number?.toLowerCase().includes(search)
        )
      }

      // Sort by last message time
      filtered.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at))

      return filtered
    }
  },
  async mounted () {
    // Check authentication
    const agentToken = localStorage.getItem('agent_token')
    if (!agentToken) {
      this.$router.push('/agent-login')
      return
    }

    await this.loadConversations()

    this.initializeNotificationSound()
    this.initializeFCMNotifications()
  },
  methods: {
    async loadConversations () {
      this.isLoading = true
      // const agentToken = localStorage.getItem('agent_token')

      try {
        // TODO: Replace with actual API endpoint when ready
        // const agentToken = localStorage.getItem('agent_token')
        // const response = await fetch('https://api.quali-d.com/agents/my-conversations', {
        //   headers: {
        //     Authorization: `Bearer ${agentToken}`,
        //     Accept: 'application/json'
        //   }
        // })

        // For now, using mock data
        await new Promise(resolve => setTimeout(resolve, 800))

        this.conversations = [
          {
            id: 1,
            customer_name: 'Ahmed Mohamed',
            phone_number: '+201234567890',
            last_message: 'Thank you for your help!',
            last_message_at: '2025-11-21 14:30:00',
            unread_count: 2,
            status: 'active'
          },
          {
            id: 2,
            customer_name: 'Sara Ali',
            phone_number: '+201112233445',
            last_message: 'When can we schedule a meeting?',
            last_message_at: '2025-11-21 13:15:00',
            unread_count: 0,
            status: 'active'
          },
          {
            id: 3,
            customer_name: 'Mohamed Hassan',
            phone_number: '+201555666777',
            last_message: 'Received, thanks!',
            last_message_at: '2025-11-21 11:00:00',
            unread_count: 0,
            status: 'resolved'
          }
        ]
      } catch (error) {
        console.error('Error loading conversations:', error)
      } finally {
        this.isLoading = false
      }
    },

    selectConversation (conversation) {
      this.selectedConversation = conversation

      // Mark as read
      conversation.unread_count = 0

      // Load messages for this conversation
      this.loadMessages(conversation.id)
    },

    async loadMessages (conversationId) {
      // TODO: Load actual messages from API
      // For now, using mock data
      this.messages = [
        {
          id: 1,
          text: 'Hello! I need some information about your services.',
          direction: 'incoming',
          timestamp: '2025-11-21 14:00:00'
        },
        {
          id: 2,
          text: 'Hi! I\'d be happy to help. What would you like to know?',
          direction: 'outgoing',
          timestamp: '2025-11-21 14:02:00'
        },
        {
          id: 3,
          text: 'What are your pricing plans?',
          direction: 'incoming',
          timestamp: '2025-11-21 14:05:00'
        },
        {
          id: 4,
          text: 'We have several plans available. Let me send you the details...',
          direction: 'outgoing',
          timestamp: '2025-11-21 14:08:00'
        },
        {
          id: 5,
          text: 'Thank you for your help!',
          direction: 'incoming',
          timestamp: '2025-11-21 14:30:00'
        }
      ]

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    async sendMessage () {
      if (!this.messageText.trim() || !this.selectedConversation) return

      const messageToSend = this.messageText.trim()
      const newMessage = {
        id: Date.now(),
        text: messageToSend,
        direction: 'outgoing',
        timestamp: new Date().toISOString()
      }

      // Add to UI immediately
      this.messages.push(newMessage)
      this.messageText = ''

      // Update conversation last message
      this.selectedConversation.last_message = newMessage.text
      this.selectedConversation.last_message_at = newMessage.timestamp

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      // Send message via API
      try {
        const agentData = localStorage.getItem('agent_data')
        const agentToken = localStorage.getItem('agent_token')

        if (!agentData || !agentToken) {
          console.error('Agent data or token not found')
          return
        }

        const parsedAgentData = JSON.parse(agentData)
        const dateTime = new Date().toISOString().slice(0, 19).replace('T', ' ')

        const response = await fetch('https://api.quali-d.com/whatsapp-conversations/send', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${agentToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            from: parsedAgentData.name || 'Agent',
            to: this.selectedConversation.phone_number.replace(/[^0-9]/g, ''),
            message: messageToSend,
            date_time: dateTime,
            platform: 'whatsapp',
            wa_id: this.selectedConversation.phone_number.replace(/[^0-9]/g, ''),
            agent_id: parsedAgentData.id,
            conversation_role: 'Agent'
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        console.log('Message sent successfully:', result)
      } catch (error) {
        console.error('Error sending message:', error)
        // Optionally show error to user
        alert('Failed to send message. Please try again.')
      }
    },

    markAsResolved () {
      if (!this.selectedConversation) return

      this.selectedConversation.status = 'resolved'
      console.log('Marked as resolved:', this.selectedConversation.id)

      // TODO: Update via API
    },

    scrollToBottom () {
      if (this.$refs.messagesArea) {
        this.$refs.messagesArea.scrollTop = this.$refs.messagesArea.scrollHeight
      }
    },

    debounceSearch () {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        // Search will be reactive through computed property
      }, 300)
    },

    initializeNotificationSound () {
      this.notificationSound = new Audio('https://dashboard.quali-d.com/sounds/notification.mp3')
      this.notificationSound.volume = 0.5
      this.notificationSound.load()

      const saved = localStorage.getItem('whatsapp_notifications_enabled')
      if (saved !== null) this.notificationEnabled = saved === 'true'

      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
      }

      document.addEventListener('click', () => {
        if (!this.audioUnlocked && this.notificationSound) {
          this.notificationSound.volume = 0
          this.notificationSound.play().then(() => {
            this.notificationSound.pause()
            this.notificationSound.currentTime = 0
            this.notificationSound.volume = 0.5
            this.audioUnlocked = true
          }).catch(() => {})
        }
      }, { once: true })
    },

    playNotificationSound () {
      if (this.notificationEnabled && this.notificationSound) {
        try {
          this.notificationSound.currentTime = 0
          this.notificationSound.play().catch(() => {})
        } catch (e) {}
      }
    },

    async initializeFCMNotifications () {
      try {
        if (!('Notification' in window)) return

        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        console.log('Agent SW registered:', registration)

        onMessageListener((payload) => {
          this.playNotificationSound()
        })

        if (Notification.permission === 'granted') {
          await this.requestAgentFCMToken()
        }

        this.fcmInitialized = true
      } catch (error) {
        console.error('Error initializing agent FCM notifications:', error)
      }
    },

    async requestAgentFCMToken () {
      try {
        const token = await requestNotificationPermission()
        if (token) {
          this.fcmToken = token
          const agentToken = localStorage.getItem('agent_token')
          if (agentToken) {
            await saveAgentFCMTokenToBackend(token, agentToken)
            console.log('✅ Agent FCM token saved')
          }
        }
      } catch (error) {
        console.error('Error requesting agent FCM token:', error)
      }
    },

    getInitials (name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    formatTime (dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m`
      if (diffHours < 24) return `${diffHours}h`
      if (diffDays < 7) return `${diffDays}d`

      return date.toLocaleDateString()
    },

    formatMessageTime (dateString) {
      const date = new Date(dateString)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.agent-whatsapp-view {
  min-height: 100vh;
  background: #f7fafc;
  padding: 24px;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  color: #4a5568;
}

.back-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 14px;
  color: #718096;
  margin: 0;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-button:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-button svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Filters */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
}

.filter-group select,
.search-wrapper input {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  background: white;
  color: #2d3748;
}

.filter-group select:focus,
.search-wrapper input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.search-wrapper input {
  padding-left: 40px;
  width: 100%;
}

/* Conversations Layout */
.conversations-layout {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 20px;
  height: calc(100vh - 240px);
}

/* Conversation List */
.conversations-list {
  background: white;
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  color: #a0aec0;
}

.loading-state svg {
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-state p,
.empty-state p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

.empty-state h3 {
  margin: 16px 0 4px 0;
  color: #4a5568;
  font-size: 18px;
}

.conversation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;
}

.conversation-item:hover {
  background: #f7fafc;
}

.conversation-item.active {
  background: #eef2ff;
  border-left: 3px solid #667eea;
}

.conversation-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.conversation-avatar.large {
  width: 40px;
  height: 40px;
}

.conversation-content {
  flex: 1;
  min-width: 0;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.conversation-header h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-time {
  font-size: 12px;
  color: #a0aec0;
  white-space: nowrap;
}

.conversation-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.conversation-preview p {
  font-size: 13px;
  color: #718096;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.unread-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #667eea;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Chat Panel */
.chat-panel {
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.no-conversation-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  color: #a0aec0;
}

.no-conversation-selected h3 {
  margin: 16px 0 4px 0;
  color: #4a5568;
  font-size: 18px;
}

.no-conversation-selected p {
  margin: 0;
  font-size: 14px;
}

/* Chat Header */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.chat-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-header-info h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 4px 0;
}

.chat-header-info p {
  font-size: 13px;
  color: #718096;
  margin: 0;
}

.chat-header-actions {
  display: flex;
  gap: 8px;
}

.icon-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

/* Messages Area */
.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f7fafc;
}

.message {
  display: flex;
  max-width: 70%;
}

.message.incoming {
  align-self: flex-start;
}

.message.outgoing {
  align-self: flex-end;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 12px;
  position: relative;
}

.message.incoming .message-bubble {
  background: white;
  border: 1px solid #e2e8f0;
  border-bottom-left-radius: 4px;
}

.message.outgoing .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-bubble p {
  margin: 0 0 4px 0;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
}

/* Message Input */
.message-input-area {
  padding: 20px;
  border-top: 1px solid #e2e8f0;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
}

.input-wrapper textarea {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  max-height: 120px;
  padding: 4px 0;
  color: #2d3748;
}

.send-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-button:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.send-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
  .conversations-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .conversations-list {
    max-height: 400px;
  }

  .chat-panel {
    min-height: 500px;
  }
}

@media (max-width: 768px) {
  .agent-whatsapp-view {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .filters-section {
    flex-direction: column;
  }

  .filter-group {
    min-width: 100%;
  }

  .message {
    max-width: 85%;
  }
}
</style>
